
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { VueComponent } from "@/vue-ts-component";

import { Action, Getter } from "vuex-class";
import {
  ISolCustomerGetAssistanceServiceResponse,
  ISolCustomerGetDeliveryServiceResponse,
  ISolCustomerGetPickUpServiceResponse,
  ISolCustomerGetServiceParams,
  ISolCustomerGetSupplyMaterialServiceResponse,
} from "@/services";
import { BModal } from "bootstrap-vue";
import {
  IAssistanceService,
  ICountryConfig,
  ISolCustomerCommodityType,
  IDeliveryService,
  IOrderConfigCommon,
  IPickUpService,
  IRequestHistorialsService,
  resolveSolCustomerOrderDescriptionKey,
  resolveSolCustomerOrderType,
  ISolCustomerCancellationReason,
  SolCustomerOrderProductCode,
  SolCustomerOrderType,
  ISolCustomerOrderService,
  ISolCustomerPack,
  ISolCustomerSupplyMaterialService,
  IUserDetailData,
  ICountryConfigResponse,
  IUserClientSubclientServicepointData,
} from "@/entities";
import { normalizeOrderSupplies } from "@/entities/sol_customer/orders/orders-detail.factory";
import VueFilters from "@/vue-filters";
import { mapGetters } from "vuex";
import DetailPickup from "@/components/orders/DetailPickup.vue";
import DetailDelivery from "@/components/orders/DetailDelivery.vue";
import DetailAssistance from "@/components/orders/DetailAssistance.vue";
import DetailSupply from "@/components/orders/DetailSupply.vue";
import DetailConsignment from "@/components/orders/DetailConsignment.vue";

@Component({
  name: "detail-order-modal",
  mixins: [VueFilters],
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    DetailConsignment,
    PModal,
    DetailPickup,
    DetailDelivery,
    DetailAssistance,
    DetailSupply,
  },
})
export default class DetailOrderModal extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;
  @Getter("getCashCountryConfig") cashCountryConfig!: ICountryConfigResponse | undefined;
  @Action("fetchPickUpService") fetchPickUpService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetPickUpServiceResponse | undefined>;
  @Action("fetchAssistanceService") fetchAssistanceService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetAssistanceServiceResponse | undefined>;
  @Action("fetchDeliveryService") fetchDeliveryService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetDeliveryServiceResponse | undefined>;
  @Action("fetchSupplyMaterialService") fetchSupplyMaterialService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetSupplyMaterialServiceResponse | undefined>;

  // Optional props
  @Prop({ required: false, default: () => [] })
  cancellationReasons!: ISolCustomerCancellationReason[];
  @Prop({ required: false, default: () => [] })
  packs!: ISolCustomerPack[];
  @Prop({ required: false, default: false })
  agilePickup!: boolean;
  @Prop({ required: false, default: false }) isOrdersTable!: boolean;
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;
  @Prop({ required: false, default: false }) showApprovalActions!: boolean;
  @Prop({ required: false, default: "" }) approvalComments!: string;
  @Prop({ required: false, default: false }) approvalLoading!: boolean;
  @Prop({ required: false, default: () => [] }) commodityTypes!: ISolCustomerCommodityType[];
  @Prop({ required: false, default: false }) isMonitoring!: boolean;

  serviceStatusCode = "";
  woStatusCode: number | undefined;
  serviceId: string | undefined;
  serviceCode: string | undefined = "";
  serviceType: number | null = null;
  orderType: SolCustomerOrderType | null = null;
  serviceTypeDesc: string | undefined = "";
  creationDate: string | null = null;
  service: ISolCustomerOrderService | null = null;
  pickUpService: IPickUpService | null = null;
  assistanceService: IAssistanceService | null = null;
  deliveryService: IDeliveryService | null = null;
  supplyMaterialService: ISolCustomerSupplyMaterialService | null = null;
  localApprovalComments = "";
  approvalCommentsInputId = "approval-comments-input";
  activeTab = 0;
  currentRawMessage: string | null = null;

  async mounted(): Promise<void> {
    this.$on("showDetailOrderModal", (data: IRequestHistorialsService) => {
      this.showModal(data);
    });
  }

  async beforeDestroy(): Promise<void> {
    this.$off("showDetailOrderModal");
  }

  async showModal(data: IRequestHistorialsService & { rawMessage?: string }): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code ?? "";
    const productCode = data.productCode?.trim();
    const serviceType = this.normalizeServiceType(data.serviceType);
    const hasOrderTypeContext = Boolean(productCode) || typeof serviceType === "number";

    this.activeTab = 0;
    this.currentRawMessage = data.rawMessage ?? null;

    this.serviceType = serviceType ?? null;
    this.resetResolvedServices();
    this.serviceId = data.serviceId;
    this.serviceCode = data.serviceCode;
    this.creationDate = data.requestedTime || null;
    this.serviceStatusCode = data.serviceStatusCode || "";
    this.woStatusCode = data.woStatusCode;
    this.service = data;
    const params = {
      countryCode: countryCode,
      serviceId: this.serviceId,
    } as ISolCustomerGetServiceParams;

    this.orderType = hasOrderTypeContext
      ? resolveSolCustomerOrderType(productCode, serviceType) || null
      : null;
    this.serviceTypeDesc = this.resolveServiceTypeDescription(data, this.orderType || undefined);
    await this.assignServiceByOrderType(data, params, this.orderType || undefined);

    this.$set(
      this.service!,
      "motiveDescription",
      this.cancellationReasons?.find((element) => element.code === this.service?.motiveCode)
        ?.description || this.service?.motiveCode
    );

    this.getBootstrapModal()?.show();
  }

  @Watch("approvalComments", { immediate: true })
  onApprovalCommentsChange(value: string): void {
    this.localApprovalComments = value || "";
  }

  onApprovalCommentsInput(value: string): void {
    this.localApprovalComments = value;
    this.$emit("update:approvalComments", value);
  }

  onApprove(): void {
    this.$emit("approve", this.localApprovalComments);
  }

  onReject(): void {
    this.$emit("reject", this.localApprovalComments);
  }

  resolveServiceTypeDescription(
    data: IRequestHistorialsService,
    orderType?: SolCustomerOrderType
  ): string {
    if (data.productCode && data.productDescription) {
      return this.$t(data.productDescription).toString();
    }

    const descriptionKey = resolveSolCustomerOrderDescriptionKey(orderType, data.isChange);
    return descriptionKey ? this.$t(descriptionKey).toString() : "";
  }

  async assignServiceByOrderType(
    data: IRequestHistorialsService,
    params: ISolCustomerGetServiceParams,
    orderType?: SolCustomerOrderType
  ): Promise<void> {
    const shouldFetchDetailById =
      !!params.serviceId &&
      !data.pendingSend &&
      (!(this.isOrdersTable || this.isMonitoring) ||
        this.shouldFetchMissingDetailInOrdersTable(data, orderType));
    switch (orderType) {
      case SolCustomerOrderType.PICKUP:
      case SolCustomerOrderType.PICKUP_OTHERS:
        this.pickUpService = data;
        if (shouldFetchDetailById) await this.getPickUpService(params);
        break;
      case SolCustomerOrderType.DELIVERY:
      case SolCustomerOrderType.DELIVERY_EXCHANGE:
        this.deliveryService = data;
        if (shouldFetchDetailById) await this.getDeliveryService(params);
        break;
      case SolCustomerOrderType.ASSISTANCE:
        this.assistanceService = data;
        if (shouldFetchDetailById) await this.getAssistanceService(params);
        break;
      case SolCustomerOrderType.SUPPLY_MATERIAL:
        this.supplyMaterialService = this.normalizeSupplyMaterialService(data);
        this.service = this.supplyMaterialService;
        if (shouldFetchDetailById) await this.getSupplyMaterialServiceService(params);
        break;
      default:
        break;
    }
  }

  async onRefreshDeclaredValues(done?: () => void): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code ?? "";
    const params = {
      countryCode: countryCode,
      serviceId: this.serviceId,
    } as ISolCustomerGetServiceParams;

    try {
      await this.getPickUpService(params);
      this.$emit("success");
    } finally {
      done?.();
    }
  }

  async getPickUpService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchPickUpService(params);
    if (res?.pickUpService) {
      this.pickUpService = res.pickUpService;
      this.service = res.pickUpService;
    }
  }

  async getAssistanceService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchAssistanceService(params);
    this.assistanceService = res?.assistanceService || null;
    this.service = res?.assistanceService || null;
  }

  async getDeliveryService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchDeliveryService(params);
    this.deliveryService = res?.deliverService || this.deliveryService;
    this.service = res?.deliverService || this.service;
  }

  async getSupplyMaterialServiceService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchSupplyMaterialService(params);
    const normalizedSupplyMaterialService = this.normalizeSupplyMaterialService(
      res?.supplyMaterialService
    );
    this.supplyMaterialService = normalizedSupplyMaterialService;
    this.service = normalizedSupplyMaterialService;
  }

  get pickupConfig(): IOrderConfigCommon {
    return this.countryConfig?.Orders.Pickup ?? {};
  }

  get deliveryConfig(): IOrderConfigCommon {
    return this.countryConfig?.Orders.Delivery ?? {};
  }

  get isPickupOrderType(): boolean {
    return (
      this.orderType === SolCustomerOrderType.PICKUP ||
      this.orderType === SolCustomerOrderType.PICKUP_OTHERS
    );
  }

  get isDeliveryOrderType(): boolean {
    return (
      this.orderType === SolCustomerOrderType.DELIVERY ||
      this.orderType === SolCustomerOrderType.DELIVERY_EXCHANGE
    );
  }

  get isAssistanceOrderType(): boolean {
    return this.orderType === SolCustomerOrderType.ASSISTANCE;
  }

  get isSupplyMaterialOrderType(): boolean {
    return this.orderType === SolCustomerOrderType.SUPPLY_MATERIAL;
  }

  get approvalCommentsLabel(): string {
    return this.$t("label.approverComments").toString();
  }

  get approvalCommentsDisplay(): string {
    const commentsFromProp = (this.approvalComments || "").trim();
    const commentsFromService = this.hasCommentsUpdated(this.service)
      ? (this.service.commentsUpdated || "").trim()
      : "";
    return commentsFromProp || commentsFromService;
  }

  get applicantDisplay(): string {
    const applicant = this.service?.applicant?.trim();
    return applicant || this.$t("label.uninformedAmount").toString();
  }

  get formattedRawMessage(): string {
    if (!this.currentRawMessage) {
      return "";
    }
    try {
      return JSON.stringify(JSON.parse(this.currentRawMessage), null, 2);
    } catch {
      return this.currentRawMessage;
    }
  }

  get modalTitle(): string {
    const desc = this.serviceTypeDesc || "";
    const code = this.serviceCode || "";

    if (desc && code) {
      return `${desc} - ${code}`;
    }
    return desc || code;
  }

  private hasCommentsUpdated(service: unknown): service is { commentsUpdated?: string | null } {
    return typeof service === "object" && service !== null && "commentsUpdated" in service;
  }

  private normalizeServiceType(value: number | undefined): number | undefined {
    if (value === undefined) return undefined;
    return Number.isInteger(value) ? value : undefined;
  }

  private shouldFetchMissingDetailInOrdersTable(
    data: IRequestHistorialsService,
    orderType?: SolCustomerOrderType
  ): boolean {
    const isPickup =
      orderType === SolCustomerOrderType.PICKUP || orderType === SolCustomerOrderType.PICKUP_OTHERS;
    if (!isPickup) {
      return false;
    }

    const productCode = data.productCode?.trim();
    if (productCode === SolCustomerOrderProductCode.PICKUP_OTHERS) {
      return true;
    }

    const hasRequestedAmounts = !!data.requestedAmounts?.length;
    return !hasRequestedAmounts;
  }

  private normalizeSupplyMaterialService(
    data:
      | ISolCustomerSupplyMaterialService
      | (IRequestHistorialsService & { supplies?: ISolCustomerSupplyMaterialService["supplies"] })
      | null
      | undefined
  ): ISolCustomerSupplyMaterialService | null {
    if (!data) {
      return null;
    }

    const normalizedSupplies =
      normalizeOrderSupplies(data.supplies, this.commodityTypes) || data.supplies || [];
    const suppliesWithFallback = normalizedSupplies.map((supply) => ({
      ...supply,
      commodityTypeDescription: supply.commodityTypeDescription || supply.commodityTypeId,
      commodityDescription: supply.commodityDescription || supply.commodityId,
    }));

    return {
      ...data,
      supplies: suppliesWithFallback,
    };
  }

  private resetResolvedServices(): void {
    this.pickUpService = null;
    this.deliveryService = null;
    this.assistanceService = null;
    this.supplyMaterialService = null;
  }

  private getBootstrapModal(): BModal | undefined {
    const pModal = this.$refs.orderDetailModal as VueComponent<any>;
    return pModal?.$children[0] as BModal | undefined;
  }
}
